import { clearGameMasterCache } from 'core/utils/localStorageUtils';
import { clearShiftRequestTypesCache } from 'core/utils/shiftRequestTypesCache';

const ORGANIZATION_ID_KEY = 'BYOD-organizationId';

/**
 * Auth and workplace keys that must not survive a session end.
 * Organization ID is preserved so the login form can prefill the tenant.
 */
const AUTH_STORAGE_KEYS = [
	'BYOD-app',
	'userId',
	'workplace-id',
	'workplace-postcode',
	'workplace_timezone',
	'workplace_name',
	'BYOD-user-role',
	'BYOD-security-roles',
	'BYOD-hierarchy-nodes',
];

/**
 * Screen-level dates/weeks persisted for in-session navigation.
 * These go stale after auto-logout and must not be restored on the next login.
 */
const SESSION_UI_STORAGE_KEYS = [
	'schedule-selected-week',
	'events-selected-date',
	'checklist-selected-date',
	'todo-selected-date',
	'logbook-selected-date',
	'events-calendar-scroll',
	'events-calendar-scroll-left',
	'events-calendar-restore-scroll',
	'scheduler-permissions',
	'cached-workplaces',
	'workplaces-map',
	'activeTab',
];

const SESSION_UI_STORAGE_PREFIXES = ['schedule-department-filter'];

const removeKeysByPrefix = (prefixes: string[]): void => {
	Object.keys(localStorage)
		.filter((key) => prefixes.some((prefix) => key.startsWith(prefix)))
		.forEach((key) => localStorage.removeItem(key));
};

/**
 * Clears persisted session caches so the next login does not restore stale
 * schedule weeks, selected dates, or workplace-scoped data.
 *
 * LocalStorage only — safe to call from the 401 interceptor, logout, and
 * login (before writing the new session). In-memory stores are not touched
 * here so a successful login cannot race against a store reset.
 */
export const clearSessionCaches = (): void => {
	try {
		const savedOrgId = localStorage.getItem(ORGANIZATION_ID_KEY);

		AUTH_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
		SESSION_UI_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
		removeKeysByPrefix(SESSION_UI_STORAGE_PREFIXES);

		clearGameMasterCache();
		clearShiftRequestTypesCache();

		if (savedOrgId) {
			localStorage.setItem(ORGANIZATION_ID_KEY, savedOrgId);
		}
	} catch (error) {
		console.warn('Failed to clear session caches:', error);
	}
};
